import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, query, addDoc, updateDoc, deleteDoc, doc, getDoc, onSnapshot, orderBy, Timestamp, serverTimestamp, writeBatch, increment, where } from 'firebase/firestore';
import { db, COLLECTIONS, SpecialPointTemplate } from '../../../utils/firebase';
import { Teacher } from '../../../utils/firebase';
import EditPencilIcon from '../../../components/icons/EditPencilIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import Button from '../../../components/ui/Button';
import AddNewPointTemplateDialog from '../../../components/ui/AddNewPointTemplateDialog';
import EditSpecialPointApprovalDialog from '../../../components/ui/EditSpecialPointApprovalDialog';
import DistributePointsDialog from '../../../components/ui/DistributePointsDialog';
import { createPointNotification } from '../../../utils/notifications';

const pxToRem = (value: number) => `${value / 16}rem`;

// SpecialPointTemplate는 firebase.ts에서 import

interface Student {
  uid: string;
  name: string;
  number?: number;
}

const SpecialPointApprovalManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  const [students, setStudents] = useState<Student[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDistributeDialog, setShowDistributeDialog] = useState(false);
  const [distributingTemplate, setDistributingTemplate] = useState<SpecialPointTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<SpecialPointTemplate | null>(null);
  const [templates, setTemplates] = useState<SpecialPointTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const totalPages = Math.ceil(templates.length / itemsPerPage);

  // 특별 포인트 템플릿 목록 가져오기
  useEffect(() => {
    if (!teacher?.schoolId || !teacher?.classId) {
      setLoading(false);
      return;
    }

    const templatesRef = collection(db, COLLECTIONS.SPECIAL_POINT_TEMPLATES.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId));
    
    // orderBy가 실패할 경우를 대비해 먼저 orderBy 없이 시도하고, 실패하면 orderBy 없이 쿼리
    let unsubscribe: (() => void) | null = null;
    let isRetrying = false;

    const processTemplates = (snapshot: any) => {
      try {
        const templatesData = snapshot.docs.map((doc: any) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            description: data.description || '',
            pointAmount: data.pointAmount || 0,
            schoolId: data.schoolId || teacher.schoolId,
            classId: data.classId || teacher.classId,
            createdBy: data.createdBy || '',
            studentIds: data.studentIds || [],
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as SpecialPointTemplate;
        }) as SpecialPointTemplate[];
        // 클라이언트 측에서 createdAt 기준으로 정렬
        templatesData.sort((a: SpecialPointTemplate, b: SpecialPointTemplate) => b.createdAt.getTime() - a.createdAt.getTime());
        setTemplates(templatesData);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('템플릿 데이터 파싱 중 오류:', err);
        setError('템플릿 데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    const tryLoadWithOrderBy = () => {
      const templatesQuery = query(templatesRef, orderBy('createdAt', 'desc'));

      unsubscribe = onSnapshot(
        templatesQuery,
        processTemplates,
        (err: any) => {
          console.error('템플릿 조회 중 오류 (orderBy 포함):', err);
          // 인덱스 오류인 경우 orderBy 없이 재시도
          if ((err?.code === 'failed-precondition' || err?.message?.includes('index')) && !isRetrying) {
            console.log('인덱스 오류 감지, orderBy 없이 재시도합니다.');
            isRetrying = true;
            if (unsubscribe) {
              unsubscribe();
            }
            tryLoadWithoutOrderBy();
          } else {
            setError(`템플릿을 불러오는 중 오류가 발생했습니다: ${err?.message || '알 수 없는 오류'}`);
            setLoading(false);
          }
        }
      );
    };

    const tryLoadWithoutOrderBy = () => {
      const templatesQuery = query(templatesRef);

      unsubscribe = onSnapshot(
        templatesQuery,
        processTemplates,
        (err: any) => {
          console.error('템플릿 조회 중 오류 (orderBy 없음):', err);
          setError(`템플릿을 불러오는 중 오류가 발생했습니다: ${err?.message || '알 수 없는 오류'}`);
          setLoading(false);
        }
      );
    };

    // 먼저 orderBy와 함께 시도
    tryLoadWithOrderBy();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [teacher?.schoolId, teacher?.classId]);

  // 날짜 포맷팅 함수
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  // 학생 수 계산 (템플릿의 studentIds 배열 길이 또는 0)
  const getStudentCount = (template: SpecialPointTemplate): number => {
    return template.studentIds?.length || 0;
  };

  // 승인 대기 중인 학생 수 계산 (실제로는 지급되지 않은 학생 수)
  const pendingCount = templates.reduce((count, template) => {
    return count + getStudentCount(template);
  }, 0);

  const handleEdit = (id: string) => {
    // 편집할 템플릿 찾기
    const template = templates.find(t => t.id === id);
    if (template) {
      setEditingTemplate(template);
      setShowEditDialog(true);
    }
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingTemplate(null);
  };

  const handleSaveEdit = async (data: {
    title: string;
    description: string;
    pointAmount: number;
  }) => {
    if (!editingTemplate || !teacher?.schoolId || !teacher?.classId) return;

    try {
      const templateRef = doc(db, COLLECTIONS.SPECIAL_POINT_TEMPLATES.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId), editingTemplate.id);
      await updateDoc(templateRef, {
        title: data.title,
        description: data.description,
        pointAmount: data.pointAmount,
        updatedAt: serverTimestamp()
      });
      
      handleCloseEditDialog();
    } catch (err) {
      console.error('템플릿 수정 중 오류:', err);
      setError('템플릿을 수정하는 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!teacher?.schoolId || !teacher?.classId) return;
    
    if (!window.confirm('이 템플릿을 삭제하시겠습니까?')) return;

    try {
      const templateRef = doc(db, COLLECTIONS.SPECIAL_POINT_TEMPLATES.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId), id);
      await deleteDoc(templateRef);
    } catch (err) {
      console.error('템플릿 삭제 중 오류:', err);
      setError('템플릿을 삭제하는 중 오류가 발생했습니다.');
    }
  };

  // 학생 목록 가져오기
  useEffect(() => {
    const fetchStudents = async () => {
      if (!teacher?.schoolId || !teacher?.classId) {
        return;
      }

      try {
        const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
        const querySnapshot = await getDocs(studentsRef);
        const fetchedStudents = querySnapshot.docs.map(doc => ({
          uid: doc.id,
          name: doc.data().name || '',
          number: doc.data().number || undefined
        })) as Student[];
        fetchedStudents.sort((a, b) => {
          const na = a.number;
          const nb = b.number;
          if (na != null && nb != null && na !== nb) {
            return na - nb;
          }
          if (na != null && nb == null) return -1;
          if (na == null && nb != null) return 1;
          return (a.name || '').localeCompare(b.name || '', 'ko');
        });
        setStudents(fetchedStudents);
      } catch (error) {
        console.error('학생 목록을 가져오는 중 오류 발생:', error);
      }
    };

    fetchStudents();
  }, [teacher?.schoolId, teacher?.classId]);

  const handleAddTemplate = () => {
    setShowAddDialog(true);
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
  };

  const handleSaveTemplate = async (data: {
    title: string;
    description: string;
    pointAmount: number;
  }) => {
    if (!teacher?.schoolId || !teacher?.classId || !teacher?.uid) return;

    try {
      // 템플릿 저장
      const templatesRef = collection(db, COLLECTIONS.SPECIAL_POINT_TEMPLATES.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId));
      await addDoc(templatesRef, {
        title: data.title,
        description: data.description,
        pointAmount: data.pointAmount,
        schoolId: teacher.schoolId,
        classId: teacher.classId,
        createdBy: teacher.uid,
        studentIds: [], // 템플릿 생성 시에는 학생 선택하지 않음
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      handleCloseDialog();
    } catch (err) {
      console.error('템플릿 저장 중 오류:', err);
      setError('템플릿을 저장하는 중 오류가 발생했습니다.');
    }
  };

  // 포인트 지급 함수
  const distributePointsToStudents = async (
    studentIds: string[],
    pointAmount: number,
    reason: string,
    distributionDate: string
  ) => {
    if (!teacher?.schoolId || !teacher?.classId) {
      throw new Error('학교 ID 또는 클래스 ID가 없습니다.');
    }

    try {
      const batch = writeBatch(db);
      
      // 날짜 문자열을 Date 객체로 변환 (YYYY-MM-DD 형식)
      const [year, month, day] = distributionDate.split('-').map(Number);
      const distributionDateObj = new Date(year, month - 1, day);
      distributionDateObj.setHours(0, 0, 0, 0);

      for (const studentId of studentIds) {
        // 학생 포인트 업데이트
        const studentRef = doc(db, COLLECTIONS.STUDENTS.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId), studentId);
        const studentDoc = await getDoc(studentRef);
        
        if (studentDoc.exists()) {
          // 학생 포인트 업데이트
          batch.update(studentRef, {
            points: increment(pointAmount)
          });

          // 포인트 지급 기록 추가
          const historyRef = doc(collection(db, COLLECTIONS.POINT_HISTORIES.replace('{schoolId}', teacher.schoolId).replace('{classId}', teacher.classId)));
          batch.set(historyRef, {
            uid: studentId,
            studentId: studentId, // StudentClassPoints에서 studentId로 조회하므로 추가
            amount: pointAmount,
            reason: reason,
            date: Timestamp.fromDate(distributionDateObj),
            type: 'oneTime',
            createdAt: serverTimestamp()
          });
        } else {
          console.warn(`학생 문서를 찾을 수 없습니다: ${studentId}`);
        }
      }

      await batch.commit();
      console.log('포인트 지급 완료:', { studentIds, pointAmount, reason, distributionDate });
    } catch (err) {
      console.error('포인트 지급 중 오류:', err);
      throw err;
    }
  };

  const handleDistributePoints = (id: string) => {
    const template = templates.find(t => t.id === id);
    if (!template) return;
    
    setDistributingTemplate(template);
    setShowDistributeDialog(true);
  };

  const handleCloseDistributeDialog = () => {
    setShowDistributeDialog(false);
    setDistributingTemplate(null);
  };

  const handleSaveDistribution = async (data: {
    selectedStudentIds: string[];
    reason: string;
    distributionDate: string;
  }) => {
    if (!distributingTemplate || !teacher?.schoolId || !teacher?.classId || !teacher?.uid || !teacher?.name) return;

    try {
      await distributePointsToStudents(
        data.selectedStudentIds,
        distributingTemplate.pointAmount,
        data.reason,
        data.distributionDate
      );

      // 각 학생에게 알림 전송
      for (const studentId of data.selectedStudentIds) {
        try {
          await createPointNotification(
            studentId,
            distributingTemplate.pointAmount,
            data.reason,
            teacher.uid,
            teacher.name,
            teacher.schoolId,
            teacher.classId
          );
        } catch (notificationError) {
          // 알림 실패해도 포인트 지급은 성공한 것으로 처리
          console.error(`학생 ${studentId}에게 알림 전송 실패:`, notificationError);
        }
      }

      alert('포인트가 지급되었습니다.');
      handleCloseDistributeDialog();
    } catch (err) {
      console.error('포인트 지급 중 오류:', err);
      setError('포인트 지급 중 오류가 발생했습니다.');
      throw err;
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 페이지네이션을 위한 아이템 슬라이싱
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTemplates = templates.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div style={{
        width: '100%',
        minHeight: 'fit-content',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: pxToRem(40),
        fontFamily: 'Pretendard, sans-serif',
        fontSize: pxToRem(14),
        color: 'var(--Color-text-assistive, #989898)'
      }}>
        로딩 중...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        minHeight: 'fit-content',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: pxToRem(40),
        fontFamily: 'Pretendard, sans-serif',
        fontSize: pxToRem(14),
        color: 'var(--Color-status-negative, #FF3838)'
      }}>
        {error}
      </div>
    );
  }

  // 페이지네이션 번호 생성
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 3;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxVisible; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };

  return (
    <div style={{
      width: '100%',
      minHeight: 'fit-content',
      display: 'flex',
      flexDirection: 'column',
      gap: pxToRem(32)
    }}>
      {/* 헤더 */}
      <div style={{
        alignSelf: 'stretch',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--Color-text-normal, #161616)',
        fontSize: pxToRem(32),
        fontFamily: 'Pretendard, sans-serif',
        fontWeight: '600',
        lineHeight: pxToRem(48),
        wordWrap: 'break-word'
      }}>
        특별 포인트
      </div>

      {/* 컨텐츠 영역 */}
      <div style={{
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: pxToRem(20),
        display: 'flex'
      }}>
        {/* 상태 표시 및 액션 버튼 */}
        <div style={{
          alignSelf: 'stretch',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(20),
          display: 'inline-flex'
        }}>
          <div style={{
            flex: '1 1 0',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: pxToRem(8),
            display: 'flex'
          }}>
            <div style={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(18),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(27),
                wordWrap: 'break-word'
              }}>
                현재
              </div>
              <div style={{
                color: 'var(--Color-fill-brand-color, #FF6F0F)',
                fontSize: pxToRem(18),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(27),
                wordWrap: 'break-word'
              }}>
                {pendingCount}
              </div>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(18),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(27),
                wordWrap: 'break-word'
              }}>
                명의 학생이 승인 대기 중 입니다
              </div>
            </div>
          </div>
          <Button
            width={pxToRem(118)}
            height={pxToRem(40)}
            variant="submit"
            onClick={handleAddTemplate}
          >
            템플릿 추가
          </Button>
        </div>

        {/* 템플릿 카드 그리드 */}
        <div style={{
          alignSelf: 'stretch',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: pxToRem(16),
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignContent: 'flex-start'
        }}>
          {currentTemplates.map((template) => (
            <div
              key={template.id}
              style={{
                width: pxToRem(416),
                height: pxToRem(293),
                paddingLeft: pxToRem(20),
                paddingRight: pxToRem(20),
                paddingTop: pxToRem(16),
                paddingBottom: pxToRem(16),
                background: 'var(--Color-background-normal, white)',
                boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                borderRadius: pxToRem(12),
                justifyContent: 'center',
                alignItems: 'center',
                gap: pxToRem(8),
                display: 'flex'
              }}
            >
              <div style={{
                width: pxToRem(376),
                alignSelf: 'stretch',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                display: 'inline-flex'
              }}>
                {/* 상단 영역 */}
                <div style={{
                  alignSelf: 'stretch',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: pxToRem(16),
                  display: 'flex'
                }}>
                  {/* 날짜 및 액션 버튼 */}
                  <div style={{
                    alignSelf: 'stretch',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'inline-flex'
                  }}>
                                        <div style={{
                                          width: pxToRem(311),
                                          color: 'var(--Color-text-minor, #BABABA)',
                                          fontSize: pxToRem(12),
                                          fontFamily: 'Pretendard, sans-serif',
                                          fontWeight: '500',
                                          lineHeight: pxToRem(18),
                                          wordWrap: 'break-word'
                                        }}>
                                          {formatDate(template.createdAt)} 생성
                                        </div>
                    <div style={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: pxToRem(5),
                      display: 'flex'
                    }}>
                      {/* 편집 버튼 */}
                      <div
                        onClick={() => handleEdit(template.id)}
                        style={{
                          width: pxToRem(20),
                          height: pxToRem(20),
                          position: 'relative',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          display: 'flex',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{
                          width: pxToRem(16),
                          height: pxToRem(16),
                          left: pxToRem(2),
                          top: pxToRem(2),
                          position: 'absolute',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          display: 'flex'
                        }}>
                          <EditPencilIcon width={16} height={16} color="var(--Color-text-assistive, #989898)" />
                        </div>
                      </div>
                      {/* 삭제 버튼 */}
                      <div
                        onClick={() => handleDelete(template.id)}
                        style={{
                          width: pxToRem(20),
                          height: pxToRem(20),
                          position: 'relative',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          display: 'flex',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{
                          width: pxToRem(16),
                          height: pxToRem(16),
                          left: pxToRem(2),
                          top: pxToRem(2),
                          position: 'absolute',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          display: 'flex'
                        }}>
                          <TrashIcon width={16} height={16} color="var(--Color-text-assistive, #989898)" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 제목 및 설명 */}
                  <div style={{
                    alignSelf: 'stretch',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: pxToRem(12),
                    display: 'flex'
                  }}>
                    <div style={{
                      alignSelf: 'stretch',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: pxToRem(12),
                      display: 'inline-flex'
                    }}>
                      <div style={{
                        flex: '1 1 0',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: pxToRem(18),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '600',
                        lineHeight: pxToRem(27),
                        wordWrap: 'break-word'
                      }}>
                        {template.title}
                      </div>
                    </div>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: pxToRem(13),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      lineHeight: pxToRem(19.5),
                      wordWrap: 'break-word',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {template.description}
                    </div>
                  </div>
                </div>

                {/* 하단 영역 */}
                <div style={{
                  alignSelf: 'stretch',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: pxToRem(12),
                  display: 'flex'
                }}>
                  {/* 포인트 지급 인원 */}
                  <div style={{
                    alignSelf: 'stretch',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: pxToRem(8),
                    display: 'flex'
                  }}>
                                        <div style={{
                                          justifyContent: 'flex-end',
                                          alignItems: 'flex-start',
                                          gap: pxToRem(20),
                                          display: 'inline-flex'
                                        }}>
                                          <div style={{
                                            color: 'var(--Color-text-assistive, #989898)',
                                            fontSize: pxToRem(14),
                                            fontFamily: 'Pretendard, sans-serif',
                                            fontWeight: '500',
                                            lineHeight: pxToRem(21),
                                            wordWrap: 'break-word'
                                          }}>
                                            포인트 지급 인원
                                          </div>
                                          <div style={{
                                            color: 'var(--Color-text-brand-color, #FF6F0F)',
                                            fontSize: pxToRem(14),
                                            fontFamily: 'Pretendard, sans-serif',
                                            fontWeight: '500',
                                            lineHeight: pxToRem(21),
                                            wordWrap: 'break-word'
                                          }}>
                                            {getStudentCount(template)}
                                          </div>
                                        </div>
                  </div>

                  {/* 포인트 및 지급 버튼 */}
                  <div style={{
                    alignSelf: 'stretch',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'inline-flex'
                  }}>
                    <div style={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: pxToRem(2),
                      display: 'flex'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-brand-color, #FF6F0F)',
                        fontSize: pxToRem(24),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '600',
                        lineHeight: pxToRem(36),
                        wordWrap: 'break-word'
                      }}>
                        {template.pointAmount}
                      </div>
                      <div style={{
                        justifyContent: 'flex-end',
                        display: 'flex',
                        flexDirection: 'column',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        포인트
                      </div>
                    </div>
                    <div
                      onClick={() => handleDistributePoints(template.id)}
                      style={{
                        height: pxToRem(32),
                        paddingLeft: pxToRem(12),
                        paddingRight: pxToRem(12),
                        paddingTop: pxToRem(8),
                        paddingBottom: pxToRem(8),
                        background: 'var(--Color-fill-normal, #293044)',
                        borderRadius: pxToRem(8),
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: pxToRem(4),
                        display: 'flex',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: pxToRem(4),
                        display: 'flex'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-inverse, white)',
                          fontSize: pxToRem(14),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: pxToRem(21),
                          wordWrap: 'break-word'
                        }}>
                          포인트 지급
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div style={{
            paddingTop: pxToRem(20),
            paddingBottom: pxToRem(20),
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: pxToRem(8),
            display: 'inline-flex'
          }}>
            {/* 첫 페이지 버튼 */}
            <div style={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex'
            }}>
              <div
                onClick={() => handlePageChange(1)}
                style={{
                  width: pxToRem(24),
                  height: pxToRem(24),
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.2 9.2L13.4 7L18 11.6L13.4 16.2L11.2 14L14.8 10.4L11.2 9.2Z" fill="var(--Color-text-assistive, #989898)" />
                  <path d="M7.2 9.2L9.4 7L14 11.6L9.4 16.2L7.2 14L10.8 10.4L7.2 9.2Z" fill="var(--Color-text-assistive, #989898)" />
                </svg>
              </div>
              <div
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  width: pxToRem(24),
                  height: pxToRem(24),
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 16.8L9.4 12.2L14 7.6L12 5.6L6 11.6L12 17.6L14 16.8Z" fill="var(--Color-text-assistive, #989898)" />
                </svg>
              </div>
            </div>

            {/* 페이지 번호 */}
            <div style={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex'
            }}>
              {getPageNumbers().map((page, index) => (
                <div
                  key={index}
                  onClick={() => typeof page === 'number' && handlePageChange(page)}
                  style={{
                    width: pxToRem(24),
                    height: pxToRem(24),
                    borderRadius: pxToRem(8),
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'inline-flex',
                    cursor: typeof page === 'number' ? 'pointer' : 'default',
                    background: typeof page === 'number' && page === currentPage 
                      ? 'transparent' 
                      : 'transparent'
                  }}
                >
                  <div style={{
                    textAlign: 'center',
                    color: typeof page === 'number' && page === currentPage
                      ? 'var(--Color-text-normal, #161616)'
                      : 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(14),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(14),
                    wordWrap: 'break-word'
                  }}>
                    {page}
                  </div>
                </div>
              ))}
            </div>

            {/* 마지막 페이지 버튼 */}
            <div style={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex'
            }}>
              <div
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  width: pxToRem(24),
                  height: pxToRem(24),
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 16.8L14.6 12.2L10 7.6L12 5.6L18 11.6L12 17.6L10 16.8Z" fill="var(--Color-text-normal, #161616)" />
                </svg>
              </div>
              <div
                onClick={() => handlePageChange(totalPages)}
                style={{
                  width: pxToRem(24),
                  height: pxToRem(24),
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.8 9.2L10.6 7L6 11.6L10.6 16.2L12.8 14L9.2 10.4L12.8 9.2Z" fill="var(--Color-text-normal, #161616)" />
                  <path d="M16.8 9.2L14.6 7L10 11.6L14.6 16.2L16.8 14L13.2 10.4L16.8 9.2Z" fill="var(--Color-text-normal, #161616)" />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 템플릿 추가 다이얼로그 */}
      <AddNewPointTemplateDialog
        isOpen={showAddDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveTemplate}
      />

      {/* 템플릿 수정 다이얼로그 */}
      <EditSpecialPointApprovalDialog
        isOpen={showEditDialog}
        onClose={handleCloseEditDialog}
        initialData={editingTemplate ? {
          title: editingTemplate.title,
          description: editingTemplate.description,
          pointAmount: editingTemplate.pointAmount
        } : undefined}
        onSave={handleSaveEdit}
      />

      {/* 포인트 지급 다이얼로그 */}
      <DistributePointsDialog
        isOpen={showDistributeDialog}
        onClose={handleCloseDistributeDialog}
        students={students}
        templateTitle={distributingTemplate?.title}
        templateDescription={distributingTemplate?.description}
        templatePointAmount={distributingTemplate?.pointAmount}
        onSave={handleSaveDistribution}
      />
    </div>
  );
};

export default SpecialPointApprovalManagement;

